import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Check, Minimize2 } from 'lucide-react';

/**
 * Jobs · fetching loader — recreated from the Claude Design handoff
 * ("Jobs · fetching loader" / JobsLoaderScreen).
 *
 * A glassy card over a blurred page, with an animated radar (pulsing rings +
 * sweeping arc + center search disc), a step ticker that advances over time,
 * and an animated progress bar.
 *
 * Props:
 *   isLoading (bool)
 *   text (string | string[] — cycles if an array)
 *   steps (string[] — optional override of the ticker labels)
 *   onMinimise (fn — optional). When supplied the overlay becomes dismissible:
 *     Esc, backdrop click and an explicit button all call it. The underlying
 *     work is unaffected; this only hides the overlay.
 *   startedAt (ms epoch — optional). Progress is measured from this instead of
 *     from mount. Callers that allow minimising MUST pass it: minimising drops
 *     `isLoading`, and mount-relative progress would restart at step one every
 *     time the user reopened the overlay, on work that never restarted.
 *
 * Without onMinimise the overlay stays modal, preserving existing callers.
 * Long-running work (the hiring-signal scan runs for minutes) must pass it —
 * a full-screen block with no exit is a trap, not a loading state.
 */

const KEYFRAMES = `
@keyframes jfl-spin { to { transform: rotate(360deg); } }
@keyframes jfl-radar { 0% { transform: scale(.4); opacity:.7 } 100% { transform: scale(1); opacity:0 } }
@keyframes jfl-dotpulse { 0%,100% { transform: scale(.6); opacity:.6 } 50% { transform: scale(1); opacity:1 } }
@keyframes jfl-prog { 0% { transform: translateX(-40%) } 100% { transform: translateX(160%) } }
.jfl-ring { position:absolute; inset:0; border-radius:9999px; border:2px solid #3F57E0; animation: jfl-radar 1.8s ease-out infinite; }
.jfl-dot { width:7px; height:7px; border-radius:9999px; background:#fff; animation: jfl-dotpulse 1s ease-in-out infinite; }
.jfl-prog { position:relative; overflow:hidden; transition: width .6s ease; }
.jfl-prog::after { content:""; position:absolute; inset:0; width:40%; background:linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: jfl-prog 1.2s ease-in-out infinite; }
`;

// How long each step is displayed before advancing. Only a pacing device — the
// backend reports no per-step progress.
const STEP_INTERVAL_MS = 1600;

const DEFAULT_STEPS = [
  'Pulling new openings',
  'Matching to your profile',
  'Scoring fit & ranking',
  'Building your feed',
];

const formatElapsed = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};

const JobsFetchingLoader = ({
  isLoading,
  text = 'Fetching jobs for you…',
  steps = DEFAULT_STEPS,
  subtitle = 'Scanning fresh roles and scoring each one for fit. This usually takes a few seconds.',
  onMinimise = null,
  startedAt = null,
}) => {
  const messages = Array.isArray(text) ? text : [text];
  const [idx, setIdx] = useState(0);
  // Progress is derived from wall-clock time since the work began, never from a
  // counter owned by this component. Minimising unmounts the overlay, and a
  // mount-relative counter restarted the ticker at step one each time it was
  // reopened — claiming the fetch had gone backwards when it had not.
  const [now, setNow] = useState(() => Date.now());
  // Fallback for callers that cannot minimise and so have no start time to give.
  const fallbackStartRef = useRef(null);

  const canMinimise = typeof onMinimise === 'function';

  const handleMinimise = useCallback(() => {
    if (canMinimise) onMinimise();
  }, [canMinimise, onMinimise]);

  useEffect(() => {
    if (!isLoading) { fallbackStartRef.current = null; return; }
    if (fallbackStartRef.current == null) fallbackStartRef.current = Date.now();
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading || !canMinimise) return;
    const onKey = (e) => { if (e.key === 'Escape') handleMinimise(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLoading, canMinimise, handleMinimise]);

  useEffect(() => {
    if (!isLoading || messages.length <= 1) return;
    const id = setInterval(() => setIdx((p) => (p + 1) % messages.length), 2000);
    return () => clearInterval(id);
  }, [isLoading, messages.length]);

  if (!isLoading) return null;

  const effectiveStart = startedAt || fallbackStartRef.current || now;
  const elapsedMs = Math.max(0, now - effectiveStart);
  // Seconds elapsed. Shown once the wait stops feeling instant, so the user can
  // tell the difference between "still working" and "hung" — the complaint that
  // prompted this was "I don't think this is working", against a scan that was
  // in fact running fine.
  const elapsed = Math.floor(elapsedMs / 1000);
  // The last step stays "in progress" until loading actually finishes — we never
  // claim it's done.
  const activeStep = Math.min(Math.floor(elapsedMs / STEP_INTERVAL_MS), steps.length - 1);
  const title = messages[idx % messages.length] || 'Fetching jobs for you…';
  // Fill proportional to how far through the steps we are (capped below 100 since
  // we don't know the true completion moment).
  const progressPct = Math.min(Math.round(((activeStep + 0.5) / steps.length) * 100), 92);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{ background: 'rgba(15,23,42,.14)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)' }}
      onClick={canMinimise ? handleMinimise : undefined}
    >
      <style>{KEYFRAMES}</style>

      {/* Loader card */}
      <div
        className="relative flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
        style={{
          zIndex: 1, padding: '40px 48px',
          // Fixed width so the card doesn't jump as the title/step text changes length.
          width: 460, boxSizing: 'border-box',
          background: 'rgba(255,255,255,.86)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid #E6E8EE', borderRadius: 20,
          boxShadow: '0 20px 50px rgba(15,23,42,.18)',
        }}
      >
        {/* Animated radar */}
        <div style={{ position: 'relative', width: 120, height: 120, marginBottom: 24 }}>
          <span className="jfl-ring" style={{ animationDelay: '0s' }} />
          <span className="jfl-ring" style={{ animationDelay: '.6s' }} />
          <span className="jfl-ring" style={{ animationDelay: '1.2s' }} />
          <div style={{
            position: 'absolute', inset: 34, borderRadius: 9999,
            background: 'linear-gradient(135deg, #3F57E0, #6478F0)',
            display: 'grid', placeItems: 'center',
            boxShadow: '0 8px 22px rgba(63,87,224,.4)',
          }}>
            <Search style={{ width: 24, height: 24, color: '#fff' }} />
          </div>
          <svg viewBox="0 0 120 120" width="120" height="120" style={{ position: 'absolute', inset: 0, animation: 'jfl-spin 1.4s linear infinite' }}>
            <defs>
              <linearGradient id="jfl-sweep" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#3F57E0" stopOpacity="0" />
                <stop offset="1" stopColor="#3F57E0" stopOpacity="1" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r="54" fill="none" stroke="url(#jfl-sweep)" strokeWidth="3" strokeLinecap="round" strokeDasharray="90 250" />
          </svg>
        </div>

        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', color: '#0F172A', textAlign: 'center', width: 360, wordBreak: 'break-word' }}>{title}</div>
        <div style={{ fontSize: 13, marginTop: 6, color: '#64748B', textAlign: 'center', maxWidth: 360 }}>
          {subtitle}
        </div>

        {/* Step ticker (advances over time) */}
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10, width: 360 }}>
          {steps.map((label, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: 9999, flexShrink: 0,
                  display: 'grid', placeItems: 'center', color: '#fff',
                  background: done ? '#10B981' : active ? '#3F57E0' : '#E6E8EE',
                  transition: 'background .3s ease',
                }}>
                  {done
                    ? <Check style={{ width: 10, height: 10 }} />
                    : active
                      ? <span className="jfl-dot" />
                      : <span style={{ width: 5, height: 5, borderRadius: 9999, background: '#CBD2DD' }} />}
                </div>
                <span style={{
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                  color: done ? '#64748B' : active ? '#0F172A' : '#94A3B8',
                }}>{label}</span>
                {done && <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 600, color: '#10B981' }}>done</span>}
              </div>
            );
          })}
        </div>

        {/* Progress */}
        <div style={{ width: 360, marginTop: 20 }}>
          <div style={{ width: '100%', height: 6, background: '#EEF1F5', borderRadius: 999, overflow: 'hidden' }}>
            <span className="jfl-prog" style={{ display: 'block', height: '100%', width: `${progressPct}%`, background: '#3F57E0', borderRadius: 999 }} />
          </div>
        </div>

        {/* Elapsed time. Only appears once the wait is long enough to worry
            about — before that it would just draw attention to nothing. */}
        {elapsed >= 10 && (
          <div style={{ marginTop: 12, fontSize: 11.5, color: '#94A3B8', textAlign: 'center', width: 360 }}>
            {formatElapsed(elapsed)} elapsed
            {elapsed >= 60 && ' — deep scans can take a few minutes'}
          </div>
        )}

        {canMinimise && (
          <button
            type="button"
            onClick={handleMinimise}
            style={{
              marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 10, border: '1px solid #E6E8EE',
              background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Minimize2 style={{ width: 13, height: 13 }} />
            Continue in background
          </button>
        )}

        {canMinimise && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#94A3B8' }}>
            The scan keeps running — press Esc to carry on using Career Champ.
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsFetchingLoader;
